import { AxiosInstance } from 'axios';
import { createApiClient } from '../http/client';
import { ApiResponse } from '../../interfaces/common';
import { CreateTeamRequest, Team, TeamDetail, UpdateTeamRequest } from '../../interfaces/calls';
import { AuthService } from '../auth';

const APPOINTMENTS_API_BASE_URL = process.env.REACT_APP_PUBLIC_APPOINTMENTS_API_BASE_URL;

class TeamsService {
  private axios: AxiosInstance;

  constructor() {
    this.axios = createApiClient({
      baseURL: APPOINTMENTS_API_BASE_URL,
    });
  }

  private getAuthHeaders() {
    return {
      Authorization: `Bearer ${AuthService.getAuth()?.auth_token}`,
    };
  }

  async getAllTeams(): Promise<ApiResponse<Team[]>> {
    try {
      const response = await this.axios.get<Team[]>('/communications/teams', {
        headers: this.getAuthHeaders(),
      });
      return {
        success: true,
        statusCode: 200,
        data: response.data,
      };
    } catch (error: any) {
      console.error('Error fetching teams:', error);
      return {
        success: false,
        statusCode: error.response?.status || 500,
        message: error.response?.data?.message || 'Failed to fetch teams',
      };
    }
  }

  async getTeam(id: number): Promise<ApiResponse<TeamDetail>> {
    try {
      const response = await this.axios.get<TeamDetail>(`/communications/teams/${id}`, {
        headers: this.getAuthHeaders(),
      });
      return {
        success: true,
        statusCode: 200,
        data: response.data,
      };
    } catch (error: any) {
      console.error('Error fetching team:', error);
      return {
        success: false,
        statusCode: error.response?.status || 500,
        message: error.response?.data?.message || 'Failed to fetch team',
      };
    }
  }

  async createTeam(data: CreateTeamRequest): Promise<ApiResponse<Team>> {
    try {
      const response = await this.axios.post<Team>('/communications/teams', data, {
        headers: this.getAuthHeaders(),
      });
      return {
        success: true,
        statusCode: 201,
        data: response.data,
      };
    } catch (error: any) {
      console.error('Error creating team:', error);
      return {
        success: false,
        statusCode: error.response?.status || 500,
        message: error.response?.data?.message || 'Failed to create team',
      };
    }
  }

  async updateTeam(id: number, data: UpdateTeamRequest): Promise<ApiResponse<Team>> {
    try {
      const response = await this.axios.put<Team>(`/communications/teams/${id}`, data, {
        headers: this.getAuthHeaders(),
      });
      return {
        success: true,
        statusCode: 200,
        data: response.data,
      };
    } catch (error: any) {
      console.error('Error updating team:', error);
      return {
        success: false,
        statusCode: error.response?.status || 500,
        message: error.response?.data?.message || 'Failed to update team',
      };
    }
  }

  async deleteTeam(id: number): Promise<ApiResponse<void>> {
    try {
      await this.axios.delete(`/communications/teams/${id}`, {
        headers: this.getAuthHeaders(),
      });
      return {
        success: true,
        statusCode: 200,
      };
    } catch (error: any) {
      console.error('Error deleting team:', error);
      return {
        success: false,
        statusCode: error.response?.status || 500,
        message: error.response?.data?.message || 'Failed to delete team',
      };
    }
  }
}

export default new TeamsService();
